import React from "react";
import { useParams } from "react-router-dom";
import { ArrowRight, Check, Download, Leaf, MessageCircle } from "lucide-react";
import { BACKEND_URL } from "../lib/api";
import {
  RENA, useRenalifePage, useRenalifeCta, Eyebrow, PageShell, LoadingScreen, NotFoundScreen, VideoBlock, Footer,
} from "./RenalifeLanding";

export default function RenalifeProducto() {
  const { slug, pid } = useParams();
  const { data, notFound } = useRenalifePage(slug);
  const { go, sending } = useRenalifeCta(slug, pid);

  if (notFound) return <NotFoundScreen />;
  if (!data) return <LoadingScreen />;

  const product = (data.products || []).find((p) => p.material_id === pid);
  if (!product) return <NotFoundScreen />;

  const asset = data.assets?.[pid] || {};
  const benefits = product.benefits || "";
  const ingredients = (product.ingredients || "").replace(/\s+y\s+(?![^()]*\))/g, ", ").split(/,\s*(?![^()]*\))/).map((s) => s.trim()).filter(Boolean);

  return (
    <PageShell testid={`renalife-producto-${pid}`}>
      {/* HERO */}
      <section className="min-h-screen flex items-center px-6 sm:px-12" style={{ background: `linear-gradient(160deg, ${RENA.deep} 0%, ${RENA.forest} 120%)` }}>
        <div className="max-w-6xl mx-auto grid lg:grid-cols-2 gap-10 items-center py-16 w-full">
          <div>
            <Eyebrow>PRODUCTO RENALIFE · {(product.focus || "").toUpperCase()}</Eyebrow>
            <h1 className="font-renalife font-bold text-4xl sm:text-5xl lg:text-6xl leading-[1.08] mt-5">
              {product.title}
            </h1>
            <div className="font-mono text-sm mt-3" style={{ color: RENA.gold }}>{product.presentation}</div>
            <p className="text-base sm:text-lg mt-6 max-w-xl" style={{ color: "#cbd5d1" }}>{benefits}</p>
            <div className="flex flex-wrap items-center gap-4 mt-9">
              <button
                data-testid="cta-product"
                onClick={go}
                disabled={sending}
                className="inline-flex items-center gap-2 rounded-full px-8 py-4 font-bold text-base transition-transform hover:-translate-y-0.5"
                style={{ background: RENA.gold, color: RENA.deep }}
              >
                <MessageCircle size={17} /> {sending ? "Abriendo WhatsApp…" : "Lo quiero — pedirlo por WhatsApp"}
              </button>
              <a
                data-testid="cta-product-pdf"
                href={`${BACKEND_URL}/api/public/renalife-pdf/${pid}`}
                target="_blank"
                rel="noreferrer"
                className="inline-flex items-center gap-2 rounded-full px-6 py-4 font-semibold text-sm border transition-colors hover:bg-white/5"
                style={{ borderColor: `${RENA.gold}60`, color: RENA.gold }}
              >
                <Download size={15} /> Descargar folleto PDF
              </a>
            </div>
            <p className="text-[11px] mt-4" style={{ color: "#64748b" }}>Este producto no es un medicamento.</p>
          </div>
          {asset.image_url ? (
            <img data-testid="product-hero-image" src={`${BACKEND_URL}${asset.image_url}`} alt={product.title} className="rounded-3xl border shadow-2xl w-full object-cover" style={{ borderColor: `${RENA.gold}30` }} />
          ) : (
            <div className="rounded-3xl border flex items-center justify-center aspect-[3/2]" style={{ borderColor: `${RENA.gold}25`, background: "#03251c" }}>
              <Leaf size={48} style={{ color: `${RENA.gold}60` }} />
            </div>
          )}
        </div>
      </section>

      {/* INGREDIENTES / QUÉ CONTIENE */}
      <section className="max-w-4xl mx-auto px-6 sm:px-12 py-20">
        <Eyebrow>¿Qué contiene?</Eyebrow>
        <h2 className="font-renalife text-2xl sm:text-3xl mt-4 mb-8">Naturaleza, sin letras chiquitas.</h2>
        <div data-testid="product-ingredients" className="grid sm:grid-cols-2 gap-3">
          {ingredients.map((ing) => (
            <div key={ing} className="flex items-start gap-2.5 rounded-xl border px-4 py-3" style={{ borderColor: "#ffffff15", background: "#03251c" }}>
              <Check size={16} className="mt-0.5 shrink-0" style={{ color: RENA.gold }} />
              <span className="text-sm capitalize" style={{ color: "#cbd5d1" }}>{ing}</span>
            </div>
          ))}
        </div>
      </section>

      <VideoBlock videoUrl={asset.video_url} title={`${product.title} en acción`} />
      {asset.video_url && (
        <p className="text-center text-[11px] -mt-10 pb-10" style={{ color: "#64748b" }}>Este producto no es un medicamento.</p>
      )}

      {/* CIERRE + CTA */}
      <section className="max-w-3xl mx-auto px-6 sm:px-12 py-16 text-center">
        <p className="font-renalife text-2xl sm:text-3xl leading-snug">
          Tu bienestar no necesita esperar al lunes.<br />
          <span style={{ color: RENA.gold }}>Pídelo hoy</span> y empieza mañana.
        </p>
      </section>
      <button
        data-testid="cta-product-final"
        onClick={go}
        disabled={sending}
        className="w-full py-8 font-renalife font-bold text-xl sm:text-3xl transition-opacity hover:opacity-90 flex items-center justify-center gap-3"
        style={{ background: RENA.gold, color: RENA.deep }}
      >
        Lo quiero — pedirlo por WhatsApp <ArrowRight size={26} />
      </button>
      <div className="text-center py-3 text-[11px]" style={{ background: RENA.deep, color: "#64748b" }}>
        Este producto no es un medicamento. El consumo de suplementos es responsabilidad de quien lo usa; consulta a tu médico.
      </div>
      <Footer name={data.distributor_name} />
    </PageShell>
  );
}
